import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { findProfilePicByPhone } from "./helpers";
import { tNegocio } from "./shared";
import { AuthContext } from "../../context/Auth/AuthContext";

import { bumpNegocio, negocioResources, resourceKey } from "./sync";
import { useNegocioLive } from "./useNegocioSync";
export { bumpNegocio, invalidateRecords } from "./sync";
export { useNegocioLive } from "./useNegocioSync";

export const useNegocioResource = (path, fetcher) => {
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId;
  const key = resourceKey(companyId, path);
  const [state, setState] = useState(() => ({ key, value: negocioResources.peek(key), loading: !negocioResources.peek(key) }));
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const mountedKey = useRef(null);
  const sequence = useRef(0);
  const reload = useCallback(async ({ soft = false } = {}) => {
    if (!companyId) return;
    const request = ++sequence.current;
    if (!soft && negocioResources.peek(key) === undefined) setState((s) => ({ ...s, key, loading: true }));
    try {
      const value = await negocioResources.load(key, fetchRef.current);
      if (mountedKey.current === key && request === sequence.current) setState({ key, value, loading: false });
    } catch (error) {
      if (mountedKey.current === key && request === sequence.current) {
        if (!error?.cancelled) toastError(error);
        setState((s) => ({ ...s, loading: false }));
      }
    }
  }, [companyId, key]);
  useEffect(() => {
    mountedKey.current = key;
    setState({ key, value: negocioResources.peek(key), loading: negocioResources.peek(key) === undefined });
    reload();
    return () => { mountedKey.current = null; sequence.current += 1; };
  }, [key, reload]);
  useNegocioLive([path], () => reload({ soft: true }));
  const current = state.key === key ? state : { value: undefined, loading: true };
  return { value: current.value, loading: current.loading, reload };
};

export const useRecords = (path) => {
  const { value, loading, reload } = useNegocioResource(path, async () => {
    const { data } = await api.get(`/negocio/${path}`);
    return Array.isArray(data?.records) ? data.records : [];
  });
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId;
  const [pictures, setPictures] = useState({ companyId, rows: new Map() });
  useEffect(() => {
    if (path !== "suppliers" && path !== "customers") return undefined;
    if (!value?.some((row) => !row.profilePicUrl && (row.number || row.phone || row.contactId))) return undefined;
    let active = true;
    // Render business records first; optional contact photos must not block them.
    negocioResources.load(resourceKey(companyId, "contact-photos"), async () => {
      const { data } = await api.get("/contacts/list", { params: { name: "" } });
      return Array.isArray(data) ? data : [];
    }, 60000).then((contacts) => {
      if (!active) return;
      const byId = new Map(contacts.map((contact) => [Number(contact.id), contact.profilePicUrl]));
      setPictures({ companyId, rows: new Map(value.map((row) => [row.id ?? row.contactId,
        row.profilePicUrl || byId.get(Number(row.contactId)) || findProfilePicByPhone(row.phone || row.number, contacts) || null
      ])) });
    }).catch(() => { /* Optional photos do not affect business records. */ });
    return () => { active = false; };
  }, [companyId, path, value]);
  const records = useMemo(() => {
    if (pictures.companyId !== companyId) return value || [];
    return (value || []).map((row) => row.profilePicUrl || !pictures.rows.get(row.id ?? row.contactId) ? row : { ...row, profilePicUrl: pictures.rows.get(row.id ?? row.contactId) });
  }, [companyId, value, pictures]);
  return { records, loading, reload };
};

export const useDialogSave = (reload) => {
  const [dialog, setDialog] = useState(null);
  const saveDialog = async () => {
    if (!dialog) return;
    const { type, item, isNew } = dialog;
    try {
      if (type === "product") {
        if (isNew) await api.post("/negocio/products", item);
        else await api.put(`/negocio/products/${item.id}`, item);
        bumpNegocio("products");
      } else if (type === "supplier") {
        if (isNew) await api.post("/negocio/suppliers", item);
        else await api.put(`/negocio/suppliers/${item.id}`, item);
        bumpNegocio("suppliers");
      } else if (type === "payment") {
        if (isNew) await api.post("/negocio/payment-methods", item);
        else await api.put(`/negocio/payment-methods/${item.id}`, item);
        bumpNegocio("payment-methods");
      } else if (type === "account") {
        if (isNew) await api.post("/negocio/accounts", item);
        else await api.put(`/negocio/accounts/${item.id}`, item);
        bumpNegocio("accounts");
      } else if (type === "sale") {
        await api.post("/negocio/sales", {
          contactId: item.contactId,
          paymentMethodId: item.paymentMethodId || null,
          notes: item.notes,
          status: item.status || "pending_payment",
          items: [{ productId: item.productId, profileId: item.profileId || null, days: item.days, price: item.price }],
        });
        bumpNegocio("sales");
      }
      toast.success(tNegocio("toastSaved"));
      setDialog(null);
      if (typeof reload === "function") reload();
    } catch (e) {
      toastError(e);
    }
  };
  return { dialog, setDialog, saveDialog };
};

export const useConfirm = (reload) => {
  const [confirm, setConfirm] = useState(null);
  const runConfirm = async () => {
    if (!confirm) return;
    try {
      if (confirm.action === "delete") {
        await api.delete(`/negocio/${confirm.resource}/${confirm.id}`);
        bumpNegocio(confirm.resource);
      } else if (confirm.action === "saleStatus") {
        await api.patch(`/negocio/sales/${confirm.id}`, { status: confirm.status });
        bumpNegocio("sales");
      } else if (confirm.action === "subStatus") {
        await api.patch(`/negocio/subscriptions/${confirm.id}`, { status: confirm.status });
        bumpNegocio("subscriptions");
      } else if (confirm.action === "renew") {
        await api.patch(`/negocio/subscriptions/${confirm.id}`, { renew: true });
        bumpNegocio("subscriptions");
      }
      toast.success(tNegocio("toastSaved"));
      setConfirm(null);
      if (typeof reload === "function") reload();
    } catch (e) {
      toastError(e);
    }
  };
  return { confirm, setConfirm, runConfirm };
};
